import { store } from '../store';
import { connected, disconnected, subscribe, unsubscribe, setError, incrementReconnectAttempts } from '../store/slices/websocketSlice';

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:3000/ws';

class WebSocketService {
  private _ws: WebSocket | null = null;
  
  get ws(): WebSocket | null {
    return this._ws;
  }
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;

  connect(token: string | null) {
    if (this._ws?.readyState === WebSocket.OPEN) {
      return;
    }

    const url = token ? `${WS_URL}?token=${token}` : WS_URL;
    this._ws = new WebSocket(url);

    this._ws.onopen = () => {
      console.log('WebSocket connected');
      store.dispatch(connected());
      this.reconnectAttempts = 0;
      this.startHeartbeat();
    };

    this._ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this._ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      store.dispatch(setError('WebSocket connection error'));
    };

    this._ws.onclose = () => {
      console.log('WebSocket disconnected');
      store.dispatch(disconnected());
      this.stopHeartbeat();
      this.attemptReconnect(token);
    };
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopHeartbeat();
    if (this._ws) {
      this._ws.close();
      this._ws = null;
    }
  }

  subscribe(channel: string) {
    if (this._ws?.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify({
        action: 'subscribe',
        channel,
      }));
      store.dispatch(subscribe(channel));
    }
  }

  unsubscribe(channel: string) {
    if (this._ws?.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify({
        action: 'unsubscribe',
        channel,
      }));
      store.dispatch(unsubscribe(channel));
    }
  }

  send(channel: string, payload: any) {
    if (this._ws?.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify({
        action: 'publish',
        channel,
        payload,
      }));
    }
  }

  private handleMessage(message: any) {
    const { type, channel, payload } = message;

    switch (type) {
      case 'project.update':
        store.dispatch(require('../store/slices/projectsSlice').updateProjectFromWS({
          id: payload.projectId,
          ...payload,
        }));
        break;

      case 'vote.cast':
        store.dispatch(require('../store/slices/votingSlice').updateResults({
          sessionId: payload.sessionId,
          results: payload.results,
        }));
        break;

      case 'chat.message':
        store.dispatch(require('../store/slices/chatSlice').addMessageFromWS({
          projectId: payload.projectId || channel.split('/')[2],
          message: payload,
        }));
        break;

      case 'chat.message.deleted':
        store.dispatch(require('../store/slices/chatSlice').deleteChatMessage(payload.messageId));
        break;

      case 'chat.message.reaction':
        // Update reaction summary for the message
        const messageId = payload.messageId;
        const reactionSummary = payload.reactionSummary;
        // Find and update the message in the store
        const state = store.getState();
        const chatState = state.chat;
        Object.keys(chatState.byProject).forEach((projectId) => {
          const projectMessages = chatState.byProject[projectId].messages;
          const messageIndex = projectMessages.findIndex(
            (m: any) => (m.id === messageId || m._id === messageId)
          );
          if (messageIndex !== -1) {
            store.dispatch({
              type: 'chat/updateMessageReactions',
              payload: {
                projectId,
                messageId,
                reactionSummary,
              },
            });
          }
        });
        break;

      case 'activity.feed':
        store.dispatch(require('../store/slices/notificationsSlice').addNotification({
          id: payload.activityId,
          type: payload.type,
          message: `${payload.type} occurred`,
          read: false,
          createdAt: payload.timestamp,
        }));
        break;

      default:
        console.log('Unknown message type:', type);
    }
  }

  private attemptReconnect(token: string | null) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    store.dispatch(incrementReconnectAttempts());

    this.reconnectTimer = setTimeout(() => {
      console.log(`Reconnecting... (attempt ${this.reconnectAttempts})`);
      this.connect(token);
    }, this.reconnectDelay);
  }

  private startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this._ws?.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // 30 seconds
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
}

export const websocketService = new WebSocketService();

